"use client";

import Link from "next/link";
import { useEffect } from "react";
import posthog from "posthog-js";
import { buttonClass } from "@/components/ui";

/**
 * Page-level error boundary — catches a thrown error anywhere under the root
 * layout (every page except the layout itself; see global-error.tsx's own
 * doc-comment for why that one exists separately). Without this, a crash in
 * a page component fell through to Next's framework-default error screen
 * with zero analytics visibility, same gap global-error.tsx already closed
 * for root-layout-level crashes — same capture + ChunkLoadError handling,
 * ported here (financecareers twin, swept same run).
 */
export default function ErrorBoundary({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error(error);
    if (posthog.__loaded) posthog.captureException(error);

    if (/ChunkLoadError|Loading chunk .* failed/i.test(`${error.name} ${error.message}`)) {
      const key = "cmk-chunk-reload";
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, "1");
        window.location.reload();
      }
    }
  }, [error]);

  return (
    <main className="mx-auto flex max-w-2xl flex-col items-center px-4 py-24 text-center sm:py-32">
      <p className="telex text-[11px] uppercase tracking-wide text-brand-700">
        changemaker
      </p>
      <h1 className="display mt-3 text-3xl font-semibold text-ink sm:text-4xl">
        Something broke on our end
      </h1>
      <p className="mt-4 text-[15px] leading-relaxed text-neutral-600">
        The page hit an unexpected error. It&rsquo;s been reported &mdash; try
        again, or head back to the job board.
      </p>
      <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
        <button type="button" onClick={() => reset()} className={buttonClass("primary")}>
          Try again
        </button>
        <Link href="/jobs" className={buttonClass("secondary")}>
          Back to jobs
        </Link>
      </div>
    </main>
  );
}
